import records from "@/data/concepts.json";
import deliveryZones from "@/data/delivery-zones.json";
export type Concept = (typeof records)[number];
export const CONCEPTS = records;
// Preserve historical records and indexed URLs; exclude retired offers from sales.
export const RETIRED_CONCEPT_SLUGS = ["parkens-aktivitetsdag"] as const;
export function isRetiredConcept(slug: string) {
  return RETIRED_CONCEPT_SLUGS.some((retired) => retired === slug);
}
export const ZONES = deliveryZones;
export const DRAFT_PREVIEW = import.meta.env.VITE_PREVIEW_DRAFT_CONCEPTS === "true";
export function visibleConcepts() {
  return CONCEPTS.filter(
    (c) => !isRetiredConcept(c.slug) && (c.contentStatus === "approved" || DRAFT_PREVIEW),
  );
}
export function getConcept(slug: string) {
  return CONCEPTS.find(
    (c) =>
      c.slug === slug &&
      (isRetiredConcept(c.slug) || c.contentStatus === "approved" || DRAFT_PREVIEW),
  );
}
export const PRICING_RULES = {
  hourlyPerPersonSek: 650,
  minimumRigHours: 1,
  roundingHours: 0.5,
  transportPerMilSek: 160,
  lightingIncluded: true,
};
export type Zone = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H" | "I" | "custom";
export function approvedPrice(concept: Concept): number | null {
  if (isRetiredConcept(concept.slug)) return null;
  if (concept.price.status !== "approved" || !concept.price.approvedAt) return null;
  const value = concept.price.amountSek;
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}
export function quoteSummary(
  selections: { slug: string; quantity: number }[],
  options: {
    zone: Zone;
    oneWayKm: number | null;
    legs: 2 | 4;
    custom: boolean;
    hasLegacyItems?: boolean;
  },
) {
  const hasRetiredSelections = selections.some((s) => isRetiredConcept(s.slug));
  const rows = selections
    .filter((s) => !isRetiredConcept(s.slug))
    .map((s) => {
      const c = CONCEPTS.find((x) => x.slug === s.slug);
      const unitPrice = c ? approvedPrice(c) : null;
      return {
        ...s,
        name: c?.name ?? s.slug,
        unitPrice,
        amount: unitPrice == null ? null : unitPrice * s.quantity,
      };
    });
  const priced =
    rows.length > 0 &&
    rows.every((r) => r.amount != null && Number.isInteger(r.quantity) && r.quantity > 0);
  const baseSubtotal = priced ? rows.reduce((sum, r) => sum + (r.amount ?? 0), 0) : null;
  const km = options.oneWayKm;
  const transport =
    km != null && Number.isFinite(km) && km >= 0 ? Math.round(km * options.legs * 16) : null;
  const needsCoordination =
    rows.length !== 1 ||
    rows[0]?.quantity !== 1 ||
    options.custom ||
    options.hasLegacyItems ||
    hasRetiredSelections ||
    options.zone === "custom";
  const total =
    baseSubtotal != null && transport != null && !needsCoordination
      ? baseSubtotal + transport
      : null;
  return {
    rows,
    baseSubtotal,
    transport,
    total,
    requiresReview: total == null,
    version: CONCEPTS[0]?.version,
  };
}
